import { PlusCircle } from "iconoir-react";
import { Trans, useTranslation } from "react-i18next";
import { SingleNumberInput, WhereaboutsModal } from "shared/components";
import { Center, Flex, styled } from "styled-system/jsx";
import { Spinner, Text, useToastContext } from "@ttc3k/trekker";
import { useRef, useState } from "react";
import { useSendOneTimeCode } from "./useSendOneTimeCode";
import { useAppContext } from "AppContext";
import { handleBeaconClick } from "shared/utils";
import { useClaimAnOperator } from "shared/hooks";

type SendOneTimeCodeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  operatorName: string;
  operatorEntityId: string;
  operatorEmail: string;
};

export const SendOneTimeCodeModal = ({
  isOpen,
  onClose,
  operatorName,
  operatorEntityId,
  operatorEmail,
}: SendOneTimeCodeModalProps) => {
  const { toastFactory } = useToastContext();
  const defaultCode = ["", "", "", "", "", ""];
  const [code, setCode] = useState(defaultCode);

  // Create refs for all inputs
  const inputRefs = useRef<(HTMLInputElement | null)[]>([
    null,
    null,
    null,
    null,
    null,
    null,
  ]);

  const { t } = useTranslation("core", {
    keyPrefix: "DOORSTOP.OPERATORS.ONE_TIME_CODE_MODAL",
  });
  const { dbUser } = useAppContext();
  const {
    codeStatus,
    onValidateConfirmationCode,
    onResendConfirmationCode,
    resendLoading,
    resetStatus,
    validating,
  } = useSendOneTimeCode();
  const { onClaimOperator, loading } = useClaimAnOperator(operatorEntityId);

  const handleSetCode = (val: string, index: number) => {
    const newCode = [...code];
    newCode[index] = val;
    setCode(newCode);

    // Focus the next input if it exists
    if (val && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }

    // Validate the code when it's complete
    if (newCode.every((val) => val.length > 0)) {
      onValidateConfirmationCode(dbUser?._id ?? "", newCode.join(""));
    }
  };

  const handleResendConfirmationCode = async () => {
    try {
      await onResendConfirmationCode(dbUser?._id ?? "", operatorEmail);
      toastFactory.create({
        title: t("SUCCESS", { keyPrefix: "" }),
        description: t("RESEND_SUCCESS", {
          email: operatorEmail,
        }),
      });
    } catch {
      toastFactory.create({
        title: t("ERROR.TITLE", { keyPrefix: "" }),
        description: t("ERROR", {
          email: operatorEmail,
        }),
      });
    }
  };

  const handleClose = () => {
    onClose();
    resetStatus();
    setCode(defaultCode);
  };

  const handleClaimOperator = async () => {
    try {
      await onClaimOperator();
      toastFactory.create({
        title: t("SUCCESS", { keyPrefix: "" }),
        description: t("CLAIM_SUCCESS", {
          email: operatorEmail,
        }),
      });
    } catch {
      toastFactory.create({
        title: t("ERROR.TITLE", { keyPrefix: "" }),
        description: t("ERROR_CLAIM"),
      });
    }
  };

  const isDirty = code.some((v) => v.length > 0);

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("TITLE", { keyPrefix: "DOORSTOP.OPERATORS.ADD_MODAL" })}
      HeaderIcon={PlusCircle}
      cancelButtonCopy={t("RESEND")}
      cancelButtonProps={{
        disabled: codeStatus === "valid",
        isLoading: resendLoading,
        onClick: handleResendConfirmationCode,
      }}
      saveButtonCopy={t("CLAIM")}
      saveButtonProps={{
        visual: "accent.blue",
        disabled: code.some((v) => v.length < 1) || codeStatus !== "valid",
        isLoading: loading,
        onClick: handleClaimOperator,
      }}
      size="md"
    >
      <Flex flexDir={"column"} gap={"200"} w={"full"}>
        <Text color={"text.mid"}>
          {t(`${isDirty ? "SHORT_" : ""}INSTRUCTIONS`)}&nbsp;
          <Text as={"span"} visual={"bodySemiBold"}>
            {operatorName}.
          </Text>
        </Text>

        <Flex w={"full"} gap={"150"} justifyContent={"space-between"}>
          {code.map((val, index) => (
            <SingleNumberInput
              key={index}
              value={val}
              onChange={(newVal) => handleSetCode(newVal, index)}
              ref={(el) => {
                inputRefs.current[index] = el; // Assign to the ref array
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "Backspace" &&
                  !val &&
                  inputRefs.current[index - 1]
                ) {
                  // Focus the previous input when user hits the backspace
                  inputRefs.current[index - 1]?.focus();
                }
              }}
              disabled={codeStatus === "valid"}
              invalid={codeStatus === "invalid"}
              valid={codeStatus === "valid"}
            />
          ))}
        </Flex>
        {validating && (
          <Center>
            <Spinner w={"40px"} h={"40px"} />
          </Center>
        )}
        {codeStatus === "invalid" && !validating && (
          <Text visual={"smallMedium"} color={"text.error.mid"}>
            <Trans
              i18nKey={
                "core:DOORSTOP.OPERATORS.ONE_TIME_CODE_MODAL.INCORRECT_CODE"
              }
              components={{
                Button: (
                  <styled.button
                    textDecoration={"underline"}
                    cursor={"pointer"}
                    onClick={handleBeaconClick}
                  />
                ),
              }}
            />
          </Text>
        )}
        {codeStatus === "valid" && !validating && (
          <Text visual={"smallMedium"} color={"text.success.mid"}>
            {t("SUCCESS", { keyPrefix: "" })}!
          </Text>
        )}
      </Flex>
    </WhereaboutsModal>
  );
};
